import { useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  PageSwitcher,
  AddPageButton,
  CollaborationBar,
  useTheme,
  AlertCircle,
} from '@deckspace/ui';
import type { SyncStateDisplay } from '@deckspace/ui';
import { useWorkspaceEvents } from '@deckspace/client';
import type { WorkspaceId } from '@deckspace/ids';
import { useApi } from '../lib/api-context';
import { createPage, renamePage, deletePage } from '../lib/queries';
import { useWorkspace } from '../hooks/useWorkspace';
import { useSurfaceMutations } from '../hooks/useSurfaceMutations';
import { queryKeys } from '../lib/queryKeys';
import { LAST_PAGE_KEY_PREFIX } from '../lib/constants';
import { PageRenderer } from './workspace/PageRenderer';
import { WorkspaceEmpty } from './workspace/WorkspaceEmpty';

export function Workspace() {
  const { id, pageId: activePageId } = useParams<{ id: string; pageId: string }>();
  const navigate = useNavigate();
  const { themeKey } = useTheme();
  const api = useApi();
  const apiUrl = import.meta.env.VITE_API_URL as string;

  // Fetch workspace detail
  const queryClient = useQueryClient();
  const { workspace, isPending, error: workspaceError } = useWorkspace(api, id);
  const { connectionStatus } = useWorkspaceEvents(apiUrl, id as WorkspaceId | undefined);

  // Get mutation callbacks
  const surfaceMutations = useSurfaceMutations(api, id);

  // Derived state
  const pages = useMemo(() => workspace?.pages ?? [], [workspace]);
  const activePage = useMemo(
    () => pages.find((p) => p.id === activePageId) ?? null,
    [pages, activePageId]
  );

  // Redirect to WorkspaceRedirect if pageId doesn't match any page (stale URL / deleted page)
  useEffect(() => {
    if (!isPending && workspace && pages.length > 0 && activePageId && !activePage) {
      navigate(`/w/${id}`, { replace: true });
    }
  }, [isPending, workspace, pages, activePageId, activePage, id, navigate]);

  // Find the surface for the active page (one surface per page).
  const activeSurface = useMemo(() => {
    if (!workspace || !activePageId) return null;
    return workspace.surfaces.find((s) => s.pageId === activePageId) ?? null;
  }, [workspace, activePageId]);

  // Sync state for CollaborationBar
  const syncState: SyncStateDisplay | undefined = useMemo(() => {
    if (!id) return undefined;
    return {
      workspaceId: id,
      status: connectionStatus,
    };
  }, [id, connectionStatus]);

  // Persist last-visited page to localStorage for redirect landing
  useEffect(() => {
    if (id && activePageId) {
      localStorage.setItem(`${LAST_PAGE_KEY_PREFIX}${id}`, activePageId);
    }
  }, [id, activePageId]);

  // Handlers
  const handlePageSelect = useCallback(
    (pageId: string) => {
      navigate(`/w/${id}/${pageId}`, { replace: true });
    },
    [id, navigate]
  );

  // Page CRUD
  const handleAddPage = useCallback(async () => {
    if (!id) return;
    const pageCount = pages.length;
    const name = pageCount === 0 ? 'Page 1' : `Page ${pageCount + 1}`;

    try {
      const newPage = await createPage(api, id, name);
      queryClient.invalidateQueries({ queryKey: queryKeys.workspace.detail(id!) });
      navigate(`/w/${id}/${newPage.id}`, { replace: true });
    } catch (err) {
      console.error('Failed to create page:', err);
    }
  }, [api, id, pages, queryClient, navigate]);

  const handleRenamePage = useCallback(
    async (pageId: string, newName: string) => {
      try {
        await renamePage(api, pageId, newName);
        queryClient.invalidateQueries({ queryKey: queryKeys.workspace.detail(id!) });
      } catch (err) {
        console.error('Failed to rename page:', err);
      }
    },
    [api, id, queryClient]
  );

  const handleDeletePage = useCallback(
    async (pageId: string) => {
      try {
        const success = await deletePage(api, pageId);
        if (success) {
          queryClient.invalidateQueries({ queryKey: queryKeys.workspace.detail(id!) });
          if (pageId === activePageId) {
            const fallbackId = pages.find((p) => p.id !== pageId)?.id;
            if (fallbackId) {
              navigate(`/w/${id}/${fallbackId}`, { replace: true });
            }
          }
        }
      } catch (err) {
        console.error('Failed to delete page:', err);
      }
    },
    [api, activePageId, pages, id, queryClient, navigate]
  );

  // Error / loading derivations
  const error = workspaceError?.message ?? null;
  const notFound = workspaceError?.message === 'Workspace not found';

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Toolbar: workspace title, page tabs */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-2">
        {workspace && (
          <h1 className="text-sm font-semibold text-foreground truncate max-w-48">
            {workspace.name || 'Untitled workspace'}
          </h1>
        )}

        {/* Page tabs */}
        {workspace && (
          <div className="flex items-center gap-1 ml-2">
            {pages.length > 0 && (
              <PageSwitcher
                pages={pages}
                activePageId={activePageId ?? null}
                onPageSelect={handlePageSelect}
                onRenamePage={handleRenamePage}
                onDeletePage={handleDeletePage}
              />
            )}
            <AddPageButton onAddPage={handleAddPage} />
          </div>
        )}

        <div className="flex-1" />

        {/* Collaboration indicator — always show when connected */}
        <CollaborationBar syncState={syncState} />
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-auto bg-background p-4">
        {/* Loading */}
        {isPending && (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}

        {/* Error */}
        {error && !notFound && (
          <div className="flex flex-col items-center gap-3 py-16 text-destructive">
            <AlertCircle className="h-6 w-6" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Not found */}
        {notFound && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-lg font-medium text-foreground">Workspace not found</p>
          </div>
        )}

        {/* No pages — empty workspace */}
        {!isPending && !error && workspace && pages.length === 0 && (
          <WorkspaceEmpty variant="no-pages" connectionStatus={connectionStatus} />
        )}

        {/* Active page view */}
        {!isPending && !error && workspace && pages.length > 0 && (
          <PageRenderer
            surface={activeSurface}
            workspaceId={id!}
            connectionStatus={connectionStatus}
            themeKey={themeKey}
            onUpdateNodeProps={surfaceMutations.updateSurfaceNodeProps}
          />
        )}
      </div>
    </div>
  );
}
